/** Scene tabs above the pictures and picture options under them. */

import { darkDemo, startDarkDemo, stopDarkDemo } from '../demo';
import { FEATURES } from '../features';
import { sceneName, t } from '../i18n';
import { gazeRelevant } from '../optics/retina';
import { getScene } from '../scenes';
import { SCENE_IDS, type SceneId } from '../scenes/types';
import { state, update } from '../state';
import { h } from './dom';
import type { Controls } from './patient';

export function buildSceneBar(): Controls {
  const lang = state.lang;
  const tabs = new Map<SceneId, HTMLButtonElement>();
  const box = h('div', { class: 'seg seg-wide scene-tabs', role: 'tablist' });
  for (const id of SCENE_IDS) {
    const btn = h('button', {
      type: 'button', class: 'seg-btn', role: 'tab', 'data-scene': id, text: sceneName(lang, id),
      onclick: () => update({ scene: id }),
    });
    tabs.set(id, btn);
    box.append(btn);
  }
  const el = h('section', { class: 'scenebar' }, h('span', { class: 'group-title', text: t(lang, 'step.scene') }), box);
  const sync = (): void => {
    for (const [id, btn] of tabs) {
      btn.classList.toggle('on', state.scene === id);
      btn.setAttribute('aria-selected', state.scene === id ? 'true' : 'false');
    }
  };
  sync();
  return { el, sync };
}

export function buildOptions(): Controls {
  const lang = state.lang;
  const demoBtn = h('button', {
    type: 'button', class: 'demo-btn', id: 'dark-demo',
    onclick: () => {
      if (darkDemo.active) {
        stopDarkDemo();
        return;
      }
      if (getScene(state.scene).lighting !== 'night') update({ scene: 'city_night' });
      startDarkDemo();
    },
  });
  const toggle = (id: string, label: string, get: () => boolean, set: (v: boolean) => void): { el: HTMLLabelElement; sync(): void } => {
    const input = h('input', { type: 'checkbox', id, onchange: () => set(input.checked) });
    const el = h('label', { class: 'switch', for: id }, input, h('span', { text: label }));
    return { el, sync: () => { input.checked = get(); } };
  };
  const tags = toggle('tags', t(lang, 'tags'), () => state.tags, (v) => update({ tags: v }));
  const zoom = toggle('zoom', t(lang, 'zoom'), () => state.zoom, (v) => update({ zoom: v }));
  const gaze = toggle('gaze', t(lang, 'gaze'), () => state.gaze, (v) => update({ gaze: v }));
  tags.el.hidden = !FEATURES.tags;
  zoom.el.hidden = !FEATURES.zoom;
  const el = h('section', { class: 'options' }, demoBtn, tags.el, zoom.el, gaze.el);
  const sync = (): void => {
    demoBtn.hidden = !(state.rp > 0);
    demoBtn.textContent = t(state.lang, darkDemo.active ? 'demo.stop' : 'demo.dark');
    gaze.el.hidden = !(FEATURES.gaze && gazeRelevant(state));
    tags.sync();
    zoom.sync();
    gaze.sync();
  };
  sync();
  return { el, sync };
}
